import { useCallback, useEffect, useSyncExternalStore } from "react";

/**
 * The till's text size on this device — compact, normal or large (founder,
 * 2026-10-15, F38 → D162: "3 steps, till only").
 *
 * The same shape as `usePosTheme` and `useDashboardTheme`, for the same reason:
 * a till is shared equipment in a room, and how large its text reads is a fact
 * about that screen and the distance it is read from, not about whoever is
 * logged into it. Persisted per DEVICE; no permission, no store data.
 *
 * Applied as `data-till-text-size` on `<body>` while a till screen is mounted —
 * the body for the reason `usePosTheme` records: every sheet and dialog the till
 * opens portals there. `app/styles.css` redeclares the platform type scale one
 * step down (compact) or up (large) under that attribute; `normal` puts NO
 * attribute on the body, so it is today's till exactly. The back office and the
 * kitchen board never mount the hook and never carry the attribute.
 */
export const TILL_TEXT_SIZES = ["compact", "normal", "large"] as const;
export type TillTextSize = (typeof TILL_TEXT_SIZES)[number];
export const DEFAULT_TILL_TEXT_SIZE: TillTextSize = "normal";

const STORAGE_KEY = "triotyTillTextSize";

export function isTillTextSize(value: unknown): value is TillTextSize {
  return (TILL_TEXT_SIZES as readonly unknown[]).includes(value);
}

function read(): TillTextSize {
  // Wrapped: a private window, cleared site data, or a browser set to block
  // storage all throw on access rather than returning null.
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return isTillTextSize(value) ? value : DEFAULT_TILL_TEXT_SIZE;
  } catch {
    return DEFAULT_TILL_TEXT_SIZE;
  }
}

let current: TillTextSize = read();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): TillTextSize {
  return current;
}

export function setTillTextSize(next: TillTextSize): void {
  if (next === current) return;
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // A screen that cannot persist the choice still honours it for this
    // session; losing the preference is not worth failing the tap.
  }
  listeners.forEach((listener) => listener());
}

/** The device's till text size, and a setter. Re-renders every subscriber. */
export function useTillTextSize(): {
  size: TillTextSize;
  setSize: (next: TillTextSize) => void;
} {
  const size = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const setSize = useCallback((next: TillTextSize) => {
    setTillTextSize(next);
  }, []);
  return { size, setSize };
}

/**
 * Puts the size on `<body>` for as long as the caller (the till's `Screen`) is
 * mounted. `normal` writes nothing; removed on unmount so the back office the
 * till exits to never wears it.
 */
export function useApplyTillTextSize(enabled = true): void {
  const { size } = useTillTextSize();
  useEffect(() => {
    if (!enabled || size === DEFAULT_TILL_TEXT_SIZE) return;
    const { body } = document;
    body.dataset.tillTextSize = size;
    return () => {
      delete body.dataset.tillTextSize;
    };
  }, [size, enabled]);
}
